import { useEffect, useMemo, useState } from "react";
import {
  Flex,
  Menu,
  Spin,
  Typography,
  Breadcrumb,
  Button,
  Pagination,
} from "antd";

import FilesTable from "@/components/tables/FilesTable";
import CustomSelect from "@/components/inputs/CustomSelect";
import CustomTreeSelect from "@/components/inputs/CustomTreeSelect";
import SearchFrom3 from "@/components/inputs/SearchFrom3";

import HomeIcon from "@/icons/HomeIcon";
import FolderIcon from "@/icons/FolderIcon";
import FilterIcon from "@/icons/FilterIcon";

import { useTableAutoHeight } from "@/hooks/useTableAutoHeight";

import { getFolders, getFolderContent, globalSearchFiles } from "@/api/files";
import { getUnitsTree } from "@/api/units";
import { getUnitEmployees, getEmployeesOptions } from "@/api/employees";

import { FILES_TYPES } from "@/constants/files";

function toUnitsUuids(units_uuids) {
  return (units_uuids || []).map((u) => u?.value ?? u?.uuid ?? u);
}

function buildMenuItems(tree, onFolderClick) {
  const map = (arr) =>
    arr.map((n) => ({
      key: n.uuid,
      icon: <FolderIcon />,
      label: (
        <span
          onClick={(e) => {
            e.stopPropagation();
            onFolderClick?.(n.uuid);
          }}
          style={{
            cursor: "pointer",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {n.name}
        </span>
      ),
      children: n.children?.length ? map(n.children) : undefined,
    }));
  return [
    {
      key: "catalog",
      icon: <HomeIcon />,
      label: (
        <span
          onClick={(e) => {
            e.stopPropagation();
            onFolderClick?.("catalog");
          }}
          style={{ cursor: "pointer" }}
        >
          Папка
        </span>
      ),
      disabled: false,
      children: map(tree),
    },
  ];
}

function collectAllKeys(items, acc = []) {
  items?.forEach((n) => {
    if (n?.key) acc.push(n.key);
    if (n?.children?.length) collectAllKeys(n.children, acc);
  });
  return acc;
}

function findPath(items, targetKey, trail = []) {
  for (const it of items || []) {
    const nextTrail = [...trail, it];
    if (it.key === targetKey) return nextTrail;
    if (it.children?.length) {
      const res = findPath(it.children, targetKey, nextTrail);
      if (res) return res;
    }
  }
  return null;
}

export default function FilePicker({
  isAdmins = false,
  onSelect,
  isCompactCatalog = false,
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  const [filters, setFilters] = useState({ unit_uuid: null, units_uuids: [] });
  const [fileType, setFileType] = useState("all");
  const [authorUuid, setAuthorUuid] = useState(null);
  const [search, setSearch] = useState("");
  const [isSearchMode, setIsSearchMode] = useState(false);

  const [unitsTree, setUnitsTree] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [optionsPending, setOptionsPending] = useState(true);

  const [treeLoading, setTreeLoading] = useState(true);
  const [menuItems, setMenuItems] = useState([]);
  const [openKeys, setOpenKeys] = useState([]);

  const [selectedFolderId, setSelectedFolderId] = useState("catalog");
  const [filesLoading, setFilesLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const flatUnits = useMemo(() => {
    const flat = [];
    (function traverse(nodes) {
      nodes?.forEach((node) => {
        flat.push(node);
        if (node.children?.length) traverse(node.children);
      });
    })(unitsTree);
    return flat;
  }, [unitsTree]);

  const { tableContainerRef, tableHeight, headerHeight } = useTableAutoHeight([
    files,
    pagination.pageSize,
  ]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setOptionsPending(true);
        const [tree] = await Promise.all([getUnitsTree({ from_unit: true })]);
        if (!mounted) return;
        setUnitsTree(tree);

        let list = [];
        if (isAdmins) {
          const { employees } = await getEmployeesOptions({
            is_dismissed: false,
          });
          list = employees.map(({ employee }) => employee);
        } else {
          const { members } = await getUnitEmployees({});
          list = members;
        }
        setAuthors(
          (list || []).map((item) => {
            const { lastname, firstname, patronymic } = item || {};
            return {
              ...item,
              label: `${lastname || ""} ${firstname || ""} ${
                patronymic || ""
              }`.trim(),
            };
          }),
        );
      } finally {
        setOptionsPending(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [isAdmins]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setTreeLoading(true);
        const folders = await getFolders(
          {
            ...(filters.units_uuids?.length
              ? { units_uuids: toUnitsUuids(filters.units_uuids) }
              : {}),
            ...(fileType ? { type_filter: fileType } : {}),
          },
          isAdmins,
        );
        if (!mounted) return;
        const items = buildMenuItems(folders, (uuid) => {
          setIsSearchMode(false);
          setSelectedFolderId(uuid);
        });
        setMenuItems(items);

        setOpenKeys(collectAllKeys(items));
      } finally {
        setTreeLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line
  }, [filters, fileType, isAdmins]);

  const fetchFolderContent = async (
    page = 1,
    pageSize = pagination.pageSize,
  ) => {
    setFilesLoading(true);
    try {
      const params = {
        uuid: selectedFolderId === "catalog" ? "" : selectedFolderId,
        page,
        perpage: pageSize,
        ...(filters.unit_uuid && { unit_uuid: filters.unit_uuid }),
        ...(filters.units_uuids?.length && {
          units_uuids: toUnitsUuids(filters.units_uuids),
        }),
        ...(authorUuid && { author_uuid: authorUuid }),
        ...(fileType && { type_filter: fileType }),
        ...(search && { search }),
      };
      const api = isSearchMode ? globalSearchFiles : getFolderContent;
      const { contents, total, page: p, perPage } = await api(params, isAdmins);
      setFiles(contents || []);
      setPagination({ current: p, pageSize: perPage, total });
    } finally {
      setFilesLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedFolderId) return;
    fetchFolderContent(1);
    // eslint-disable-next-line
  }, [selectedFolderId, filters, authorUuid, fileType, search, isAdmins]);

  const handleMenuClick = ({ key }) => {
    setIsSearchMode(false);
    setSelectedFolderId(key);
  };

  const handleTableChange = (pag) =>
    fetchFolderContent(pag.current, pag.pageSize);

  const handlePaginationChange = (current, pageSize) => {
    const shouldResetPage = pageSize !== pagination.pageSize;
    fetchFolderContent(shouldResetPage ? 1 : current, pageSize);
  };

  const breadcrumbItems = useMemo(() => {
    const path = findPath(menuItems, selectedFolderId) || [
      { key: "catalog", label: "Папка" },
    ];
    return path.map((node, idx) => {
      const isLast = idx === path.length - 1;
      const title = (
        <span
          onClick={() => !isLast && setSelectedFolderId(node.key)}
          style={{ cursor: isLast ? "default" : "pointer" }}
        >
          <Flex gap={6} align="center">
            {node.key === "catalog" && <HomeIcon />}
            <span>{node.label}</span>
          </Flex>
        </span>
      );
      return { title };
    });
  }, [menuItems, selectedFolderId]);

  const renderFilters = ({ mobile = false, includeSearch = true } = {}) => (
    <Flex
      vertical={mobile}
      gap={12}
      align={mobile ? undefined : "center"}
      style={mobile ? { width: "100%" } : undefined}
    >
      <CustomTreeSelect
        placeholder="Рабочая группа"
        data={unitsTree}
        value={filters.units_uuids}
        onChange={(units_uuids) => {
          setFilters({ ...filters, units_uuids });
          setFileType("all");
          setSelectedFolderId("catalog");
        }}
        style={mobile ? { width: "100%" } : { width: 220 }}
      />

      <CustomSelect
        placeholder="Тип"
        options={FILES_TYPES}
        style={mobile ? { width: "100%" } : { width: 150 }}
        popupMatchSelectWidth={false}
        value={fileType}
        onChange={(val) => {
          setFileType(val);
          if (val === "all") {
            setFilters({ ...filters, units_uuids: [] });
          } else if (val === "root") {
            setFilters({
              ...filters,
              units_uuids: flatUnits.filter(({ readOnly }) => !readOnly),
            });
          } else {
            setFilters({
              ...filters,
              units_uuids: flatUnits.filter(({ readOnly }) => readOnly),
            });
          }
          setSelectedFolderId("catalog");
        }}
      />

      <CustomSelect
        placeholder="Автор"
        value={authorUuid}
        options={authors}
        showSearch
        allowClear
        fieldNames={{ value: "uuid" }}
        optionFilterProp="label"
        style={mobile ? { width: "100%" } : { width: 220 }}
        onChange={(val) => {
          setAuthorUuid(val);
          setSelectedFolderId("catalog");
        }}
        popupRender={(menu) => <>{menu}</>}
      />

      {includeSearch && (
        <SearchFrom3
          placeholder="Поиск"
          defaultValue={search}
          style={mobile ? { width: "100%" } : { flex: 1, minWidth: 200 }}
          onSearch={(value) => {
            setSearch(value);
            setIsSearchMode(!!value);
            setSelectedFolderId("catalog");
          }}
        />
      )}
    </Flex>
  );

  return optionsPending ? (
    <div className="spin">
      <Spin size="large" />
    </div>
  ) : (
    <Flex vertical gap={20} style={{ height: "100%" }}>
      {/* Фильтры */}
      {isCompactCatalog ? (
        <Flex vertical gap={12}>
          <Flex gap={12} align="center">
            <SearchFrom3
              placeholder="Поиск"
              defaultValue={search}
              style={{ flex: 1, minWidth: 0 }}
              onSearch={(value) => {
                setSearch(value);
                setIsSearchMode(!!value);
                setSelectedFolderId("catalog");
              }}
            />

            <Button
              icon={<FilterIcon />}
              onClick={() => setIsFiltersExpanded((prev) => !prev)}
              style={{ flexShrink: 0 }}
              type={isFiltersExpanded ? "default" : "primary"}
            />
          </Flex>

          {isFiltersExpanded &&
            renderFilters({ mobile: true, includeSearch: false })}
        </Flex>
      ) : (
        renderFilters()
      )}

      {/* Контент */}
      <Flex gap={16} style={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
        {!isCompactCatalog && (
          <div style={{ width: 260, overflow: "auto" }}>
            {treeLoading ? (
              <div className="spin">
                <Spin />
              </div>
            ) : (
              <Menu
                mode="inline"
                items={menuItems}
                selectedKeys={selectedFolderId ? [selectedFolderId] : []}
                openKeys={openKeys}
                onOpenChange={setOpenKeys}
                onClick={handleMenuClick}
                inlineIndent={10}
                style={{ height: "100%", overflow: "auto" }}
              />
            )}
          </div>
        )}

        <Flex
          vertical
          gap={15}
          style={{ flex: 1, minWidth: 0, overflow: "hidden" }}
        >
          <Breadcrumb items={breadcrumbItems} />
          {filesLoading ? (
            <div className="spin">
              <Spin />
            </div>
          ) : files?.length ? (
            <>
              <div
                ref={tableContainerRef}
                style={{ flex: 1, minHeight: 0, overflow: "hidden" }}
              >
                <FilesTable
                  data={files}
                  loading={filesLoading}
                  pagination={false}
                  onChange={handleTableChange}
                  onEditItem={undefined}
                  onDeleteItem={undefined}
                  unit_uuid={null}
                  show_description
                  resize
                  scroll={{ x: "100%", y: tableHeight - headerHeight }}
                  mode="picker"
                  isCompactCatalog={isCompactCatalog}
                  onOpenFolder={(uuid) => {
                    setIsSearchMode(false);
                    setSearch("");
                    setSelectedFolderId(uuid);
                  }}
                  onPickFile={(file) => {
                    setSelectedFile(file);
                    onSelect?.(file);
                  }}
                  rowSelection={{
                    type: "radio",
                    selectedRowKeys: selectedFile ? [selectedFile.uuid] : [],
                    onChange: (_keys, rows) => {
                      const file = rows?.[0];
                      if (file?.element_type === "file") {
                        setSelectedFile(file);
                        onSelect?.(file);
                      }
                    },
                    renderCell: (checked, record, _index, originNode) => {
                      if (record.element_type !== "file") return null;
                      return originNode;
                    },
                    columnTitle: "",
                    columnWidth: 40,
                  }}
                  rowClassName={(record) =>
                    record.uuid === selectedFile?.uuid
                      ? "ant-table-row-selected"
                      : ""
                  }
                />
              </div>

              <Flex
                justify="flex-end"
                align="center"
                style={{
                  minHeight: 32,
                }}
              >
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  onChange={handlePaginationChange}
                />
              </Flex>
            </>
          ) : (
            <Typography.Text type="secondary">
              Нет файлов в этой папке.
            </Typography.Text>
          )}
          {selectedFile && (
            <div style={{ marginTop: "auto" }}>
              <Typography.Text>
                Выбран:&nbsp;
                <strong>
                  {selectedFile.name}
                  {selectedFile.extension ? `.${selectedFile.extension}` : ""}
                </strong>
              </Typography.Text>
            </div>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}
